<template>
  <div class="safety-mode">
    <Modal class="show" :open="enterOpen" @closeModal="closeEnter">
      <div class="entermodal">
        <UIIcon name="cloud-unavailable" />
        <h1 class="f4 tc">{{ $t("Connection Lost, Safety Mode Activated") }}</h1>
        <div class="f-body">
          <p>{{ $t("Temporary changes to the Launcher:") }}</p>
          <ul>
            <li>{{ $t("Filtering, searching for games and calling for help are disabled.") }}</li>
            <li>{{ $t("The timer is hidden in the Launcher but will be shown as soon as Safety Mode is turned off.") }}</li>
            <li>{{ $t("If a new session is started while the station is disconnected, you will need to track time yourself.") }}</li>
          </ul>
          <p>{{ $t("Temporary changes to the Station Monitor:") }}</p>
          <ul>
            <li>{{ $t("Editing reservations and station settings are disabled.") }}</li>
            <li>{{ $t("You will be notified when the connection is restored.") }}</li>
          </ul>
          <i18n path="Read this {article} to learn more." tag="p">
            <a
              class="link-style"
              slot="article"
              href="https://svr.li/k/234"
              rel="noopener"
              target="_blank"
            >{{ $t("Safety Mode article") }}</a>
          </i18n>
        </div>
        <UIButton @click="closeEnter">{{ $t("Got it!") }}</UIButton>
      </div>
    </Modal>
    <Modal class="show" :open="exitOpen" :showCloseBtn="false" @closeModal="closeExit">
      <div class="exitmodal">
        <UIIcon name="cloud-checked" />
        <h1 class="f4 tc">{{ $t("Reconnected, All Functionality Restored") }}</h1>
        <div class="f-body">
          <p>{{ $t("Your stations will continue to be in Safety Mode until you restore them individually in order to prevent your customers from getting their session ended unintentionally:") }}</p>
          <ul>
            <li>{{ $t("Make sure you have a running session in the Station Monitor for each station currently being used.") }}</li>
            <li>{{ $t("If you don't have a running session, create a new walk-in, adjust the time accordingly and start it.") }}</li>
            <li>{{ $t("Then restore your station.") }}</li>
          </ul>
          <i18n path="Read this {article} to learn more." tag="p">
            <a
              class="link-style"
              slot="article"
              href="https://svr.li/k/234"
              rel="noopener"
              target="_blank"
            >{{ $t("Safety Mode article") }}</a>
          </i18n>
        </div>
        <div class="timercontainer f-body tc orange">
          <div>{{ $t("Please wait while we reconnect to your stations.") }}</div>
          <div class="timer f4">{{ $t("Time remaining:") }} {{ canExitSecondsLeft }} {{ $tc('Second | Seconds', canExitSecondsLeft) }}</div>
        </div>
        <UIButton :disabled="!canExit" @click="closeExit">{{ $t("Got it!") }}</UIButton>
      </div>
    </Modal>
    <SafetyModeRefreshAlert v-if="!isNetworkAvailable"/>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import Modal from '@/core/shared/components/Modal';
import SafetyModeRefreshAlert from './SafetyModeRefreshAlert';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';

@Component({
  props: {
    isNetworkAvailable: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Modal,
    SafetyModeRefreshAlert,
    UIButton,
    UIIcon,
  },
  watch: {
    isNetworkAvailable (newVal, oldVal) {
      if (newVal && !oldVal) { // Exit SafetyMode
        this.enterOpen = false;
        this.exitOpen = true;
        this.canExitSecondInterval = setInterval(() => {
          this.canExitSecondsLeft--;
          if (this.canExitSecondsLeft < 1) {
            this.canExit = true;
            clearInterval(this.canExitSecondInterval);
          }
        }, 1000);
      } else if (!newVal && oldVal) { // Enter SafetyMode
        this.enterOpen = true;
        this.exitOpen = false;
        this.canExit = false;
        this.$emit('enterSafetyMode');
      }
    },
  },
})
export default class SafetyMode extends Vue {
  enterOpen = !this.isNetworkAvailable;
  exitOpen = false;
  canExit = false;
  canExitSecondsLeft = 60;
  canExitSecondInterval = null;

  closeEnter () {
    this.enterOpen = false;
  }

  closeExit () {
    this.exitOpen = false;
    this.canExitSecondsLeft = 60;
  }
}
</script>

<style lang="postcss" scoped>
@import 'core/shared/styles';

.safety-mode {
  & svg {
    fill: var(--colorFjord);
    margin: 0 auto;
    width: 7rem;
    height: 7rem;
  }

  & ul {
    display: grid;
    margin: var(--spacingSm) 0;
    grid-gap: var(--spacingSm);
    list-style-type: disc;
    padding-left: 3rem;
  }

  & .timer {
    margin-top: var(--spacingSm);
  }

  & .entermodal,
  & .exitmodal {
    display: grid;
    grid-gap: var(--spacingMd);
    padding: var(--spacingMd);
    max-width: 50rem;
  }
}
</style>
